import { useMemo, useState } from 'react';

import { CheckStatus, EntityRefUtil, GetChecksByAssetResponseDTO, isCheckCompleted } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import clockVector from '@/assets/clock.svg';
import CheckDrawer from '@/components/checks/check-drawer';
import ArrowLeftIcon from '@/components/icons/arrow-left';
import CheckIcon from '@/components/icons/check';
import ClockIcon from '@/components/icons/clock';
import { EllipsisHorizontalIcon } from '@/components/icons/ellipsis-horizontal';
import { InformationIcon } from '@/components/icons/information';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { getAssetByIdQueryOptions } from '@/queries/assets';
import { getChecksByAssetQueryOptions } from '@/queries/checks';
import { formatScheduleCheckLabel } from '@/utils/schedules';

type Check = GetChecksByAssetResponseDTO['checks'][number];

export function AWAScheduleChecksAssetPage() {
  const { workspaceId, assetId } = useParams();
  const navigate = useNavigate();
  const [selectedCheckId, setSelectedCheckId] = useState<string | null>(null);
  const [isCheckDrawerOpen, setIsCheckDrawerOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'open' | 'closed'>('open');

  const { data: assetData, isLoading: assetLoading } = useQuery(getAssetByIdQueryOptions({ assetId, isAWA: true }));

  const { data: checksData, isLoading: checksLoading } = useQuery(getChecksByAssetQueryOptions({ assetId: assetId! }));

  const openChecks = useMemo(() => {
    if (!checksData?.checks) return [];
    return checksData.checks.filter((check) => check.status === CheckStatus.Open).sort((a, b) => (b.dueProgress ?? -1) - (a.dueProgress ?? -1));
  }, [checksData]);

  const closedChecks = useMemo(() => {
    if (!checksData?.checks) return [];
    return checksData.checks
      .filter((check) => check.status === CheckStatus.Closed)
      .sort((a, b) => new Date(b.completedAt ?? 0).getTime() - new Date(a.completedAt ?? 0).getTime());
  }, [checksData]);

  const displayedChecks = viewMode === 'open' ? openChecks : closedChecks;

  // Use isLoading for initial load state (shows skeletons)
  const isLoading = assetLoading || checksLoading;

  // For the header title, only show "Loading..." if we don't have asset data yet
  // This prevents flickering during background refetches when data is being revalidated
  const headerTitle =
    viewMode === 'closed'
      ? 'Closed Check Record'
      : assetData
        ? EntityRefUtil.createAssetLabel(assetData)
        : assetLoading
          ? 'Loading asset details...'
          : 'Schedule Checks';

  return (
    <div className="flex h-[100dvh] w-full flex-col gap-2">
      <div className="border-b-grey-3 flex items-center justify-between border-b-2 bg-white px-4 py-4">
        <div className="flex items-center gap-1">
          <button aria-label="Go back" onClick={() => (viewMode === 'closed' ? setViewMode('open') : navigate(`/${workspaceId}/schedule-checks`))}>
            <ArrowLeftIcon size={30} />
          </button>
          <div className="line-clamp-1 text-lg font-medium">{headerTitle}</div>
        </div>
        {viewMode === 'open' && (
          <Button className="p-1" type="button" variant="ghost" onClick={() => setIsMenuOpen(true)}>
            <EllipsisHorizontalIcon />
          </Button>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-3 px-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[100px] w-full" />
          ))}
        </div>
      )}

      {/* No Checks view */}
      {!isLoading && displayedChecks.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <img src={clockVector} alt="No check records" />
          <div className="text-lg font-medium">{viewMode === 'open' ? 'No check records created yet' : 'No completed checks yet'}</div>
          <div className="text-secondary-text px-8 text-center text-sm">
            {viewMode === 'open'
              ? 'Deploy a schedule to your asset to get started'
              : 'Completed checks will appear here once a schedule form has been submitted'}
          </div>
        </div>
      )}

      {/* Checks list view */}
      {!isLoading && displayedChecks.length > 0 && (
        <div className="space-y-3 px-2">
          {displayedChecks.map((check) => (
            <CheckCard
              key={check.id}
              check={check}
              onClick={() => {
                setSelectedCheckId(check.id);
                setIsCheckDrawerOpen(true);
              }}
            />
          ))}
        </div>
      )}

      <CheckDrawer
        checkId={selectedCheckId}
        open={isCheckDrawerOpen}
        onOpenChange={(open, success) => {
          setIsCheckDrawerOpen(open);
          if (!open && success) {
            setViewMode('closed');
          }
        }}
      />

      {/* Bottom menu to view closed checks */}
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent side="bottom" hideCloseIcon className="rounded-t-xl pb-[30px]">
          <div className="bg-grey-4 mx-auto mt-[8px] mb-[18px] h-1 w-10 rounded-full" />
          <Button
            type="button"
            variant="ghost"
            className="mx-4 flex w-full items-center justify-start gap-2 rounded-lg text-left"
            onClick={() => {
              setViewMode('closed');
              setIsMenuOpen(false);
            }}>
            <ClockIcon size={30} className="py-1" />
            <span className="text-sm">View closed check records</span>
          </Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CheckCard({ check, onClick }: { check: Check; onClick?: () => void }) {
  const triggerLabel = formatScheduleCheckLabel(check);

  return (
    <div className="cursor-pointer rounded-lg border bg-white p-2" onClick={onClick}>
      <div className="flex flex-col items-start justify-between gap-1">
        <div className="text-sm font-medium">{check.schedule?.name || 'Check'}</div>
        <div className="text-sm">Schedule Check: {triggerLabel ?? <span className="text-secondary-text">-</span>}</div>
        <Badge variant={check.status === CheckStatus.Closed ? 'success' : 'grey'} size="sm">
          {check.status === CheckStatus.Closed ? 'Closed' : 'Open'}
        </Badge>
        {isCheckCompleted(check) ? <CheckIcon className="text-default-green h-5 w-5" /> : <CheckProgressBar check={check} />}
      </div>
    </div>
  );
}

function CheckProgressBar({ check }: { check: Check }) {
  if (check.isUnlinked) {
    return (
      <div className="mt-1 flex w-full items-center">
        <div className="bg-grey-2 h-3 flex-1 rounded-l-full" />
        <InformationIcon className="text-grey-5 mx-1 h-4 w-4 shrink-0" />
        <div className="bg-grey-2 h-3 flex-1 rounded-r-full" />
      </div>
    );
  }

  // No progress value
  if (check.dueProgress == null) {
    return <span className="text-secondary-text text-sm">-</span>;
  }

  const progressStatus = (() => {
    if (check.dueProgress >= 100 || (check.dateRemaining && check.dateRemaining.days <= 0)) return 'overdue';
    if (check.dueProgress <= 75) return 'ok';
    return 'due';
  })();

  const colors = match(progressStatus)
    .with('overdue', () => ({
      primary: 'rgb(var(--default-red))',
      secondary: 'rgb(var(--secondary-red))',
    }))
    .with('due', () => ({
      primary: 'rgb(var(--default-yellow))',
      secondary: 'rgb(var(--secondary-yellow))',
    }))
    .with('ok', () => ({
      primary: 'rgb(var(--default-green))',
      secondary: 'rgb(var(--secondary-green))',
    }))
    .otherwise(() => ({
      primary: 'rgb(var(--default-green))',
      secondary: 'rgb(var(--secondary-green))',
    }));

  return (
    <div className="bg-grey-2 h-3 w-full overflow-hidden rounded-full">
      <div
        className="h-full rounded-full"
        style={{
          width: check.dueProgress < 0 ? 0 : `max(12px, ${Math.min(check.dueProgress, 100)}%)`,
          background: `repeating-linear-gradient(
            45deg,
            ${colors.primary},
            ${colors.primary} 8px,
            ${colors.secondary} 8px,
            ${colors.secondary} 16px
          )`,
        }}
      />
    </div>
  );
}
